import React, { useState, useEffect } from 'react';
import { Container, Typography, Paper, List, ListItem, ListItemText, IconButton, Box, Badge, Chip, Divider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Layout from '../components/layout/Layout';
import { formatDistance } from 'date-fns';
import useNotificationSound from '../hooks/useNotificationSound';
import { useNotifications } from '../context/NotificationContext';

const NotificationsPage = () => {
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    deleteNotification: deleteNotificationFromContext,
    fetchNotifications 
  } = useNotifications();
  const { playNotificationSound } = useNotificationSound();

  // Initial fetch when component mounts
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (id) => {
    try {
      await markAsRead(id);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNotificationFromContext(id);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const formatTimeAgo = (date) => {
    return formatDistance(new Date(date), new Date(), { addSuffix: true });
  };

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Badge badgeContent={unreadCount} color="error" sx={{ mr: 2 }}>
            <NotificationsIcon fontSize="large" color="primary" />
          </Badge>
          <Typography variant="h4" component="h1" gutterBottom>
            Notifications
          </Typography>
        </Box>
        
        <Paper sx={{ p: 2, mb: 4 }}>
          {notifications.length > 0 ? (
            <List>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id || index}>
                  <ListItem
                    alignItems="flex-start"
                    secondaryAction={
                      <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(notification.id)}>
                        <DeleteIcon />
                      </IconButton>
                    }
                    sx={{ 
                      bgcolor: notification.read ? 'inherit' : 'rgba(25, 118, 210, 0.08)',
                      '&:hover': { bgcolor: notification.read ? 'rgba(0, 0, 0, 0.04)' : 'rgba(25, 118, 210, 0.12)' }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography variant="subtitle1" component="div">
                            {notification.title}
                          </Typography>
                          <Box>
                            {!notification.read && (
                              <IconButton 
                                size="small" 
                                color="primary" 
                                onClick={() => handleMarkAsRead(notification.id)}
                                sx={{ mr: 1 }}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            )}
                            <Chip 
                              size="small" 
                              label={notification.type || 'Info'} 
                              color={
                                notification.type === 'ORDER' ? 'success' : 
                                notification.type === 'ALERT' ? 'error' : 'primary'
                              }
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography
                            sx={{ display: 'block' }}
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {notification.message}
                          </Typography>
                          <Typography
                            sx={{ display: 'block', mt: 1 }}
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            {formatTimeAgo(notification.createdAt)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < notifications.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <Typography variant="body1" color="text.secondary">
                No notifications to display
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Layout>
  );
};

export default NotificationsPage; 